import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Spinner,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { TransactionStatusBadge } from '@/components/features/transactions/status-badge';
import { useAdminTransaction } from '@/hooks/use-admin-transactions';
import { formatDate, formatCents, truncate } from '@/lib/utils';

export function TransactionDetailPage() {
  const { t } = useTranslation('transactions');
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Spec 0027: merchantId from route-state (passed by transaction-table navigate)
  const merchantId = (location.state as { merchantId?: string })?.merchantId;

  const { data: transaction, isLoading, error, refetch } = useAdminTransaction(transactionId, merchantId);

  if (!transactionId) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Missing transaction ID</p>
        <Button variant="link" onClick={() => navigate('/transactions')}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!transaction) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Transaction not found</p>
        <Button variant="link" onClick={() => navigate('/transactions')}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  const sortedWebhooks = [...(transaction.webhookHistory || [])].sort((a, b) => {
    if (a.step != null && b.step != null) return a.step.localeCompare(b.step);
    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  });

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('detail.title')}
          </h1>
          <p className="mt-1 font-mono text-sm text-muted-foreground">
            {truncate(transaction.transactionId, 24)}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate('/transactions')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('detail.back')}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('detail.basicInfo')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.transactionId')}</p>
              <p className="font-mono">{transaction.transactionId}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.merchant')}</p>
              <p className="font-mono">{transaction.merchantId}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.service')}</p>
              <p className="font-mono">{transaction.serviceId}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.status')}</p>
              <TransactionStatusBadge status={transaction.status} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.flow')}</p>
              <p>{transaction.flowType}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.amount')}</p>
              <p>{transaction.grossAmount != null ? formatCents(transaction.grossAmount) : '-'}</p>
            </div>
            {transaction.destination && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.msisdn')}</p>
                <p className="font-mono">{transaction.destination}</p>
              </div>
            )}
            {transaction.operatorId && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.operator')}</p>
                <p>{transaction.operatorId}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">{t('detail.created')}</p>
              <p>{formatDate(transaction.createdAt)}</p>
            </div>
            {transaction.lastUpdate && (
              <div>
                <p className="text-sm text-muted-foreground">{t('detail.lastUpdate')}</p>
                <p>{formatDate(transaction.lastUpdate)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('detail.webhookHistory')}</CardTitle>
          </CardHeader>
          <CardContent>
            {sortedWebhooks.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('detail.noWebhooks')}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('detail.webhookColumns.step')}</TableHead>
                    <TableHead>{t('detail.webhookColumns.type')}</TableHead>
                    <TableHead>{t('detail.webhookColumns.statusCode')}</TableHead>
                    <TableHead>{t('detail.webhookColumns.timestamp')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedWebhooks.map((wh, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{wh.step ?? '-'}</TableCell>
                      <TableCell>{wh.eventType}</TableCell>
                      <TableCell>{wh.statusCode ?? '-'}</TableCell>
                      <TableCell>{formatDate(wh.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
